import React, { useState, useEffect } from "react";
import Modal from "../Modal";
import { getInvoiceTransactions } from "../../apis/completedPayments";
import ReceiptSlipModal, { ReceiptSlipData } from "./ReceiptSlipModal";
import { format } from "date-fns";

type Transaction = {
  allocationId: number;
  transactionId: number;
  receiptNumber: string | null;
  paymentDate: string | null;
  amount: number;
  paymentMode: string;
};

type TransactionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: number | null;
};

const TransactionsModal: React.FC<TransactionsModalProps> = ({
  isOpen,
  onClose,
  invoiceId,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedReceiptData, setSelectedReceiptData] = useState<ReceiptSlipData | null>(null);

  useEffect(() => {
    if (!isOpen || !invoiceId) {
      setData(null);
      setError(null);
      setSelectedReceiptData(null);
      return;
    }

    setLoading(true);
    setError(null);
    getInvoiceTransactions(invoiceId)
      .then((res: any) => {
        const result = res?.data ?? res;
        setData(result);
      })
      .catch((err: any) => {
        const msg =
          err?.response?.data?.error ??
          err?.response?.data?.message ??
          err?.message ??
          "Failed to load transactions";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [isOpen, invoiceId]);

  const invoice = data?.invoice;
  const transactions: Transaction[] = data?.transactions ?? [];

  if (selectedReceiptData) {
    return (
      <ReceiptSlipModal
        isOpen={true}
        onClose={() => setSelectedReceiptData(null)}
        receiptData={selectedReceiptData}
      />
    );
  }

  if (!isOpen) return null;

  return (
    <>
      <Modal
        title="View Transactions"
        onClose={onClose}
        width="sm:max-w-2xl"
        modalContent={(
          <div>
            {loading && (
              <div className="py-12 text-center text-gray-500">
                Loading transactions...
              </div>
            )}
            {error && (
              <div className="py-8 text-center text-red-600">{error}</div>
            )}
            {!loading && !error && data && (
              <>
                {invoice && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Invoice:</span>
                      <span className="font-medium">{invoice.invoiceNumber}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">Patient:</span>
                      <span className="font-medium">{invoice.patientName}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-medium">₹{invoice.totalAmount}</span>
                    </div>
                  </div>
                )}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Date
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Receipt No
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Mode
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Amount
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transactions.map((tx) => (
                        <tr key={tx.allocationId}>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {tx.paymentDate
                              ? format(new Date(tx.paymentDate), "dd MMM yyyy")
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {tx.receiptNumber ?? "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {tx.paymentMode ?? "-"}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-right">
                            ₹{tx.amount}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedReceiptData({
                                  patientName: invoice.patientName ?? "",
                                  receiptNumber: tx.receiptNumber ?? "",
                                  invoiceNumber: invoice.invoiceNumber ?? "",
                                  paymentDate: tx.paymentDate,
                                  paymentMode: tx.paymentMode ?? "",
                                  amount: tx.amount,
                                  clinicName: invoice.clinicName,
                                });
                              }}
                              className="text-sm font-semibold text-primary hover:underline"
                            >
                              View Receipt
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {transactions.length === 0 && (
                  <p className="py-6 text-center text-gray-500 text-sm">
                    No transactions found.
                  </p>
                )}
                <div className="flex justify-end mt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      />
    </>
  );
};

export default TransactionsModal;
